import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { UserPic } from '../../../../Post/UserPic';
import { Tag, Popconfirm, Table, message, Dropdown, Modal, Button } from 'antd';
import { DeleteOutlined, FolderOutlined, MoreOutlined, EditOutlined, SwapOutlined, TeamOutlined } from '@ant-design/icons';
import { withRouter } from 'react-router-dom';
import {
  actionAccountSpacesGet,
  actionAccountSpaceAdd,
  actionAccountSpaceGet,
  actionPostsGet,
} from '../../../actions';
import moment from 'moment';
import { actionPostDelete } from '../../../../Post/actions';
import { getTags } from '../../helpers.js';
import './style.css';
import { ERROR_GENERIC } from '../../../../../configs/constants';
import { IDENTITY_API, POSTS_API } from '../../../../../configs/env';
import { AkukoAPIService } from '../../../../../services/serviceClass';
import { genericWriteAccessHandler } from '../../../../Profile/components/ProfileMenu/helpers';
import CreateFolderModal from './components/CreateFolderModal';
import RenameFolderModal from './components/RenameFolderModal';
import SpaceBreadcrumb from './components/SpaceBreadcrumb';
import SpaceHeader from './components/SpaceHeader';
import { PostFolderService } from '../../../../../services/PostFolderService';
import MovePostModal from './components/MovePostModal';
import FolderPermissionsModal from './components/FolderPermissionsModal';

const mapStateToProps = (state, ownProps) => {
  if (state) {
    return state;
  }
  return null;
};

const mapDispatchToProps = {
  actionAccountSpaceAdd,
  actionAccountSpacesGet,
  actionAccountSpaceGet,
  actionPostsGet,
  actionPostDelete,
};

const SpaceView = (props) => {
  const [showLoader, setShowLoader] = useState(false);
  const [posts, setPosts] = useState([]);
  const [folders, setFolders] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [folderToRename, setFolderToRename] = useState(null);
  const [showMovePostModal, setShowMovePostModal] = useState(false);
  const [postToMove, setPostToMove] = useState(null);
  const [showFolderPermissionsModal, setShowFolderPermissionsModal] = useState(false);
  const [folderForPermissions, setFolderForPermissions] = useState(null);
  const params = useParams();

  const loadFolders = async (folderId = null) => {
    try {
      const service = new PostFolderService(`space/${params.id}/posts/folders/list${folderId ? `?parent_folder_id=${folderId}` : ''}`);
      const currentFolders = await service.listFolders();
      setFolders(currentFolders);
    } catch (error) {
      message.error(error?.message || 'Failed to load folders');
    }
  };

  const loadPosts = async (folderId = null) => {
    try {
      // Load posts for the selected folder
      const postsService = new AkukoAPIService(POSTS_API, `post/space/${params.id}/folder${folderId ? `?folder_id=${folderId}` : ''}`);
      const data = await postsService.list();
      props.actionPostsGet(data);
      setPosts(data);
    } catch (error) {
      message.error(error?.message || 'Failed to load posts');
    }
  }

  const loadInitialContent = async () => {
    setShowLoader(true);

    try {
      const service = new AkukoAPIService(IDENTITY_API, 'space');
      const space = await service.read(params.id);
      props.actionAccountSpaceGet(space);

      // Check if we're in a folder view
      const pathParts = window.location.pathname.split('/');
      const isFolderView = pathParts.includes('folder');
      const folderId = isFolderView ? pathParts[pathParts.length - 1] : null;

      if (folderId) {
        // Load folder details
        const folderService = new PostFolderService(`space/${params.id}/posts/folder`);
        const folder = await folderService.getFolderDetails(folderId);
        setSelectedFolder(folder);
        
        // Load folder path
        let currentFolder = folder;
        const path = [currentFolder];
        while (currentFolder.parent_id) {
          currentFolder = await folderService.getFolderDetails(currentFolder.parent_id);
          path.unshift(currentFolder);
        }
        setFolderPath(path);
        
        // Load folders and posts for the current folder
        await Promise.all([loadFolders(folderId), loadPosts(folderId)]);
      } else {
        // Load root level folders and posts
        await Promise.all([loadFolders(), loadPosts()]);
      }
    } catch (error) {
      if (error?.message === 'Unauthorized') {
        const service = new AkukoAPIService(POSTS_API, `post/space/${params.id}/public`);
        const posts = await service.list();
        props.actionPostsGet(posts);
        setPosts(posts);
      } else {
        message.error(error?.message || ERROR_GENERIC);
      }
    } finally {
      setShowLoader(false);
    }
  };

  React.useMemo(() => {
    loadInitialContent();
  }, [params.id]);

  const handleSearch = (text) => {
    setSearchText(text.toLowerCase());
  };

  const handleCreateFolder = async (values) => {
    try {
      setShowLoader(true);
      if (selectedFolder) {
        const createChildFolderService = new PostFolderService(`space/${params.id}/posts/child/folder/create`);
        await createChildFolderService.createChildFolder(values.name, selectedFolder.id);
      } else {
        const createRootFolderService = new PostFolderService(`space/${params.id}/posts/root/folder/create`);
        await createRootFolderService.createRootFolder(values.name);
      }
      await loadFolders(selectedFolder?.id);
      message.success('Folder created successfully', 2);
      setShowCreateFolderModal(false);
    } catch (error) {
      message.error(error?.message || ERROR_GENERIC);
    } finally {
      setShowLoader(false);
    }
  };

  const handleFolderClick = async (folder) => {
    try {
      setShowLoader(true);
      if (!folder) {
        setSelectedFolder(null);
        setFolderPath([]);
        await Promise.all([loadFolders(null), loadPosts(null)]);
        // Remove folder_id from URL when going to root
        props.history.replace(`/space/${params.id}/posts`);
        setShowLoader(false);
        return;
      }
      await Promise.all([loadFolders(folder.id), loadPosts(folder.id)]);
      
      if (folderPath.length > 0) {
        const folderIndex = folderPath.findIndex(f => f.id === folder.id);
        if (folderIndex !== -1) {
          // Keep only the path up to and including the clicked folder
          setFolderPath(folderPath.slice(0, folderIndex + 1));
        } else {
          // If folder not in path, append it
          setFolderPath([...folderPath, folder]);
        }
      } else {
        // First folder selection
        setFolderPath([folder]);
      }
      setSelectedFolder(folder);
      // Add folder_id to URL when selecting a folder
      props.history.replace(`/space/${params.id}/folder/${folder.id}`);
    } catch (error) {
      message.error(error?.message || 'Failed to load folder details');
    } finally {
      setShowLoader(false);
    }
  };

  const handleDeletePost = async (record) => {
    try {
      const service = new AkukoAPIService(POSTS_API, `post/${record.uuid}`);
      await service.delete();
      props.actionPostDelete({
        uuid: record.uuid,
      });
      await loadPosts(selectedFolder?.id);
      message.success('Post deleted', 2);
    } catch (error) {
      message.error(error?.message || ERROR_GENERIC);
    }
  };

  const handleRenameFolder = async (values) => {
    try {
      const service = new PostFolderService(`space/${params.id}/posts/folder/${folderToRename.id}/update`);
      await service.updateFolder(values);
      await loadFolders();
      message.success('Folder renamed successfully', 2);
      setShowRenameFolderModal(false);
      setFolderToRename(null);
      return true;
    } catch (error) {
      message.error(error?.message || 'Failed to rename folder');
      return false;
    }
  };

  const handleDeleteFolder = async (folder) => {
    try {
      const service = new PostFolderService(`space/${params.id}/posts/folder/${folder.id}`);
      await service.delete();
      await loadFolders(selectedFolder?.id);
      message.success('Folder deleted successfully', 2);
      
      // If we deleted the currently selected folder, go back to root
      if (selectedFolder?.id === folder.id) {
        setSelectedFolder(null);
        setFolderPath([]);
      }
    } catch (error) {
      message.error(error?.message || 'Failed to delete folder');
    }
  };

  const handleMovePost = async (folderId, path) => {
    try {
      const service = new AkukoAPIService(POSTS_API, `post/${postToMove.uuid}/space/${params.id}/folder`);
      await service.create(folderId ? { folder_id: folderId } : {});
      
      // Navigate to the target folder
      if (folderId) {
        const folderService = new PostFolderService(`space/${params.id}/posts/folder`);
        const targetFolder = await folderService.getFolderDetails(folderId);
        setSelectedFolder(targetFolder);
        setFolderPath(path);
        await Promise.all([loadFolders(targetFolder?.id), loadPosts(targetFolder?.id)]);
        // Update URL to include the target folder ID
        props.history.replace(`/space/${params.id}/folder/${folderId}`);
      } else {
        // Navigate to root
        setSelectedFolder(null);
        setFolderPath([]);
        await Promise.all([loadFolders(), loadPosts()]);
        // Update URL to root posts view
        props.history.replace(`/space/${params.id}/posts`);
      }
      
      message.success('Post moved successfully', 2);
      setShowMovePostModal(false);
      setPostToMove(null);
    } catch (error) {
      message.error(error?.message || 'Failed to move post');
    }
  };

  const getFolderMenu = (folder) => {
    // Check if user has admin permissions for this folder
    if (!folder.metadata?.permissions?.admin) {
      return [];
    }

    const items = [
      {
        key: 'rename',
        label: 'Rename',
        icon: <EditOutlined />,
        onClick: () => {
          setFolderToRename(folder);
          setShowRenameFolderModal(true);
        },
      },
      {
        key: 'permissions',
        label: 'Manage Access',
        icon: <TeamOutlined />,
        onClick: () => {
          setFolderForPermissions(folder);
          setShowFolderPermissionsModal(true);
        },
      },
      {
        type: 'divider',
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => {
          Modal.confirm({
            title: 'Delete Folder',
            content: `Are you sure you want to delete "${folder.name}"? This action cannot be undone.`,
            okText: 'Yes',
            cancelText: 'No',
            onOk: () => handleDeleteFolder(folder),
            centered: true,
            maskClosable: true,
            okButtonProps: { danger: true }
          });
        },
      },
    ];

    return items;
  };

  const hasFolderEditAccess = (post) => {
    // Check if we're in a folder view and the post belongs to the current folder
    if (selectedFolder && post.folder_id === selectedFolder.id) {
      return selectedFolder.metadata?.permissions?.edit || false;
    }
    return false;
  };

  const hasFolderAdminAccess = (post) => {
    // Check if we're in a folder view and the post belongs to the current folder
    if (selectedFolder && post.folder_id === selectedFolder.id) {
      return selectedFolder.metadata?.permissions?.admin || false;
    }
    return false;
  };

  const hasGenericWriteAccess = (post) => {
    // Only check generic write access if post is not in any folder
    if (!post.folder_id) {
      return genericWriteAccessHandler(props.user, post.space_id, post.author, true);
    }
    return false;
  };

  const hasGenericAdminAccess = (post) => {
    // Only check generic admin access if post is not in any folder
    if (!post.folder_id) {
      return genericWriteAccessHandler(props.user, post.space_id, post.author, false);
    }
    return false;
  };

  const getPostMenu = (post) => {
    const hasEditFromFolder = hasFolderEditAccess(post);
    const hasAdminFromFolder = hasFolderAdminAccess(post);
    const hasWriteAccess = hasGenericWriteAccess(post);
    const hasAdminAccess = hasGenericAdminAccess(post);

    // No access at all
    if (!hasEditFromFolder && !hasAdminFromFolder && !hasWriteAccess && !hasAdminAccess) {
      return [];
    }
    // If user has admin access to the folder or generic admin access (for non-folder posts), show all actions
    if (hasAdminFromFolder || (!post.folder_id && hasAdminAccess)) {
      return [
        {
          key: 'move',
          icon: <SwapOutlined />,
          label: 'Move to Folder',
          onClick: () => {
            setPostToMove(post);
            setShowMovePostModal(true);
          }
        },
        {
          type: 'divider',
          key: 'divider-1'
        },
        {
          key: 'edit',
          icon: <EditOutlined />,
          label: 'Edit Post',
          onClick: () => {
            props.history.push(`/post/${post.uuid}/edit`);
          }
        },
        {
          type: 'divider',
          key: 'divider-2'
        },
        {
          key: 'delete',
          icon: <DeleteOutlined />,
          label: 'Delete',
          danger: true,
          onClick: () => {
            Modal.confirm({
              title: 'Are you sure you want to delete this post?',
              okText: 'Yes',
              cancelText: 'No',
              onOk: () => handleDeletePost(post),
              centered: true,
              maskClosable: true,
              modalStyle: {
                top: '50%',
                transform: 'translateY(-50%)'
              }
            });
          },
        }
      ];
    }

    // If user has only generic write access, show only edit action
    if (!post.folder_id && hasWriteAccess) {
      return [
        {
          key: 'edit',
          icon: <EditOutlined />,
          label: 'Edit Post',
          onClick: () => {
            props.history.push(`/post/${post.uuid}/edit`);
          }
        }
      ];
    }

    // If user only has folder edit permissions, show only edit action
    if (hasEditFromFolder) {
      return [
        {
          key: 'edit',
          icon: <EditOutlined />,
          label: 'Edit Post',
          onClick: () => {
            props.history.push(`/post/${post.uuid}/edit`);
          }
        }
      ];
    }

    return [];
  };

  const getActionMenu = (record) => {
    return record.isFolder ? getFolderMenu(record) : getPostMenu(record);
  };

  if (props.space.id) {
    const tableData = [
      ...folders
        .filter(folder => 
          !searchText || folder.name.toLowerCase().includes(searchText)
        )
        .map(folder => ({
          ...folder,
          isFolder: true,
          title: folder.name,
          updated: folder.updated_at,
          key: `folder-${folder.id}`
        })),
      ...posts
        .filter(post => 
          !searchText || post.title.toLowerCase().includes(searchText)
        )
        .map(post => ({ 
          ...post, 
          isFolder: false,
          key: `post-${post.uuid}`
        }))
    ];

    return (
      <div className="dashboard">
        <div className="spaces">
          <SpaceBreadcrumb
            accountId={props.space.account_id}
            spaceName={props.space.name}
            folderPath={folderPath}
            onFolderClick={handleFolderClick}
          />
          <h1>Posts</h1>
          <SpaceHeader
            user={props.user}
            accountId={props.space.account_id}
            canEdit={genericWriteAccessHandler(props.user, props.space.id, undefined, true)}
            isSpaceAdmin={genericWriteAccessHandler(props.user,  props.space.id, false, false)}
            spaceId={props.space.id}
            selectedFolder={selectedFolder}
            onCreateFolder={() => setShowCreateFolderModal(true)}
            onSearch={handleSearch}
            onShareFolder={() => {
              setFolderForPermissions(selectedFolder);
              setShowFolderPermissionsModal(true);
            }}
          />
          <div className="table-wrapper">
            <Table
              pagination={{
                defaultPageSize: 20,
              }}
              loading={showLoader}
              columns={[
                {
                  title: '',
                  width: '10px',
                  render: (record) => {
                    return record.isFolder ? (
                      <FolderOutlined style={{ fontSize: '16px', color: '#111' }} />
                    ) : (
                      <UserPic author={record.author} />
                    );
                  },
                },
                {
                  title: 'Title',
                  dataIndex: 'title',
                  key: 'title',
                  render: (text, record) => {
                    return record.isFolder ? (
                      <a onClick={() => handleFolderClick(record)}>{text}</a>
                    ) : (
                      <Link to={`/post/${record.uuid}`}>{text}</Link>
                    );
                  },
                },
                {
                  title: 'Updated',
                  dataIndex: 'updated',
                  key: 'updated',
                  defaultSortOrder: 'descend',
                  sorter: (a, b) => moment(a.updated) - moment(b.updated),
                  render: (text, record) => {
                    return (
                      <span className="updated">
                        {moment(text).format('MMM DD YYYY - HH:mm')}
                      </span>
                    );
                  },
                },
                {
                  title: 'Tags',
                  dataIndex: 'tags',
                  filters: getTags(props.posts),
                  onFilter: (value, record) => {
                    if (!record.isFolder && record.tags) {
                      return record.tags.includes(value);
                    }
                    return false;
                  },
                  key: 'tags',
                  render: (text, record) => {
                    if (!record.isFolder) {
                      if (record.tags && record.tags.length) {
                        return (
                          <>
                            {record.tags.map((tag) => (
                              <Tag key={tag} color={'blue'}>
                                {tag}
                              </Tag>
                            ))}
                          </>
                        );
                      }
                      return <Tag>None</Tag>;
                    }
                    return null;
                  },
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 48,
                  align: 'right',
                  render: (_, record) => {
                    const items = getActionMenu(record);
                    if (items.length === 0) return null;

                    return (
                      <Dropdown
                        key={record.key}
                        menu={{ items }}
                        trigger={['click']}
                        placement="bottomRight"
                      >
                        <Button 
                          type="text"
                          key={record.key}
                          icon={<MoreOutlined />}
                          style={{ marginRight: -8 }}
                        />
                      </Dropdown>
                    );
                  },
                },
              ]}
              dataSource={tableData}
            />
          </div>
        </div>
        <CreateFolderModal
          open={showCreateFolderModal}
          parentFolder={selectedFolder}
          spaceName={props.space.name}
          onCancel={() => setShowCreateFolderModal(false)}
          onSubmit={handleCreateFolder}
        />
        {folderToRename && (
          <RenameFolderModal
            open={showRenameFolderModal}
            folder={folderToRename}
            onCancel={() => {
              setShowRenameFolderModal(false);
              setFolderToRename(null);
            }}
            onSubmit={handleRenameFolder}
            loading={showLoader}
          />
        )}
        {postToMove && (
          <MovePostModal
            open={showMovePostModal}
            spaceId={params.id}
            spaceName={props.space.name}
            currentFolderId={postToMove.folder_id || null}
            onCancel={() => {
              setShowMovePostModal(false);
              setPostToMove(null);
            }}
            onSubmit={handleMovePost}
            user={props.user}
          />
        )}
        {showFolderPermissionsModal && (
          <FolderPermissionsModal
            open={showFolderPermissionsModal}
            spaceId={params.id}
            accountId={props.space.account_id}
            folderId={folderForPermissions.id}
            folderName={folderForPermissions.name}
            readOnly={!folderForPermissions.metadata?.permissions?.admin || false}
            onCancel={() => {
              setShowFolderPermissionsModal(false);
              setFolderForPermissions(null);
            }}
          />
        )}
      </div>
    );
  } else {
    return null;
  }
};

const SpaceContainer = connect(mapStateToProps, mapDispatchToProps)(SpaceView);

export default withRouter(SpaceContainer);